import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select as MuiSelect,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { useFormikContext } from 'formik';
import React, { Fragment } from 'react';
import { capitalize } from 'lodash';
import { RoleIcon } from '../../../../../common/icons/RoleIconList';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { CreateActivityGetReportTypesV2 } from '../__generated__/CreateActivityGetReportTypesV2';
import { CREATE_ACTIVITY_GET_REPORT_TYPES_V2 } from '../activity-form-model-v2.gql';
import { ActivityFormValues } from '../ActivityFormV2';

const joiningPolicyOptions = [
  {
    value: true,
    label: 'Require approval',
    instruction: 'Volunteers will require manager approval when applying to join the team',
  },
  {
    value: false,
    label: 'Open activity',
    instruction: 'Any volunteer can join the team instantly',
  },
];

const rosterTypeOptions = [
  {
    value: false,
    label: 'Managed',
    instruction:
      'Volunteers indicate availability, managers or activity leaders are responsible for selecting the team and publishing the roster',
  },
  {
    value: true,
    label: 'Open',
    instruction: 'Volunteers can add and remove themselves from the roster freely',
  },
];

export const PrivacyTeamStep = () => {
  const { values, setFieldValue } = useFormikContext<ActivityFormValues>();
  const { data: rolesData } = useCampfireQuery<CreateActivityGetReportTypesV2, null>(
    CREATE_ACTIVITY_GET_REPORT_TYPES_V2
  );
  const activityRoles = rolesData?.vm.activityRoles ?? [];

  return (
    <Box>
      <Typography style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Privacy, Joining &amp; Team</Typography>
      <Typography variant='body2' color='textSecondary' style={{ marginBottom: 24 }}>
        Choose how volunteers find, join, and are rostered for this activity.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <Typography style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Joining policy</Typography>
          <FormControl component='fieldset'>
            <RadioGroup
              name='isRestrictedActivity'
              value={values.isRestrictedActivity}
              onChange={() => setFieldValue('isRestrictedActivity', !values.isRestrictedActivity)}
            >
              {joiningPolicyOptions.map(({ value, label, instruction }) => (
                <Fragment key={label}>
                  <Box display='flex' alignItems='flex-start' mb={1}>
                    <Radio
                      color='primary'
                      checked={values.isRestrictedActivity === value}
                      value={value}
                      style={{ marginTop: -6, padding: 8 }}
                    />
                    <Box ml={0.5}>
                      <Typography style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{label}</Typography>
                      <Typography style={{ fontSize: 12, color: '#808080' }}>{instruction}</Typography>
                    </Box>
                  </Box>
                </Fragment>
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Roster type</Typography>
          <FormControl component='fieldset'>
            <RadioGroup
              name='hasOpenRoster'
              value={values.hasOpenRoster}
              onChange={() => setFieldValue('hasOpenRoster', !values.hasOpenRoster)}
            >
              {rosterTypeOptions.map(({ value, label, instruction }) => (
                <Fragment key={label}>
                  <Box display='flex' alignItems='flex-start' mb={1}>
                    <Radio
                      color='primary'
                      checked={values.hasOpenRoster === value}
                      value={value}
                      style={{ marginTop: -6, padding: 8 }}
                    />
                    <Box ml={0.5}>
                      <Typography style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{label}</Typography>
                      <Typography style={{ fontSize: 12, color: '#808080' }}>{instruction}</Typography>
                    </Box>
                  </Box>
                </Fragment>
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box display='flex' alignItems='center'>
            <Switch
              size='small'
              color='primary'
              checked={values.isHidden}
              onChange={(e) => setFieldValue('isHidden', e.target.checked)}
            />
            <Box ml={1}>
              <Typography style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>Hidden activity</Typography>
            </Box>
          </Box>
          <Box pl={6}>
            <Typography style={{ fontSize: 12, color: '#808080' }}>
              {`Keep this activity hidden from volunteers who aren't on the team`}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Maximum team size</Typography>
          <Typography style={{ fontSize: 12, color: '#808080', marginBottom: 12 }}>
            Limits the total number of volunteers that can join this activity. Once reached, new volunteers are added to
            the waitlist. This is different from session volunteer limits, which control how many volunteers can be
            rostered onto each individual session.
          </Typography>
          <TextField
            type='number'
            variant='outlined'
            size='small'
            onChange={(e) => setFieldValue('maxTeam', e.target.value === '' ? null : parseInt(e.target.value, 10))}
            value={values.maxTeam ?? ''}
            name='maxTeam'
            placeholder='No limit'
            inputProps={{ step: '1', min: '1' }}
            style={{ width: 200 }}
          />
        </Grid>

        {activityRoles.length > 0 && (
          <Grid item xs={12}>
            <Typography style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Restrict by role</Typography>
            <Typography style={{ fontSize: 12, color: '#808080', marginBottom: 12 }}>
              Only volunteers with at least one of the selected roles can see this activity
            </Typography>
            <MuiSelect
              multiple
              displayEmpty
              variant='outlined'
              value={values.requiredRoles.map((r) => r.activityRoleId)}
              onChange={(event) => {
                const selectedIds = event.target.value as string[];
                const selectedRoles = activityRoles.filter((r) => selectedIds.includes(r.activityRoleId));
                setFieldValue('requiredRoles', selectedRoles);
              }}
              renderValue={(selected) => {
                const roleIds = selected as string[];
                if (roleIds.length === 0) {
                  return <Typography style={{ fontSize: 14, color: '#808080' }}>All roles (no restriction)</Typography>;
                }
                return (
                  <Box display='flex' flexWrap='wrap' style={{ gap: 4 }}>
                    {roleIds.map((roleId) => {
                      const role = activityRoles.find((r) => r.activityRoleId === roleId);
                      return role ? (
                        <Chip
                          key={role.activityRoleId}
                          size='small'
                          color='primary'
                          label={capitalize(role.name)}
                          icon={
                            role.icon ? (
                              <RoleIcon
                                iconName={role.icon}
                                style={{ width: 16, height: 16, marginRight: -4, marginLeft: 6 }}
                              />
                            ) : (
                              undefined
                            )
                          }
                        />
                      ) : null;
                    })}
                  </Box>
                );
              }}
              style={{ maxWidth: 400 }}
            >
              {activityRoles
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((role) => (
                  <MenuItem key={role.activityRoleId} value={role.activityRoleId}>
                    <Checkbox
                      color='primary'
                      size='small'
                      checked={values.requiredRoles.some((r) => r.activityRoleId === role.activityRoleId)}
                    />
                    <Typography style={{ fontSize: 14 }}>{role.name}</Typography>
                  </MenuItem>
                ))}
            </MuiSelect>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
